import { Avatar, Image, SelectCustom } from '@/components';
import { CommentMethod, LotterySocketMethod, PriceSymbol } from '@/layouts/shared';
import { forEach } from 'lodash';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useFetchCommentByIdQuery, useFetchCommentsQuery } from '../../../api';
import { Comment, MessageData, MessageDataSocket, PinnedProductTypes } from '../../../const';
import { getCommentAuthor } from '@/features/livestream/detail/util';
import { ReplyComment } from './ReplyComment';
import { humanTimeDate, formatTimeHMSS } from '@/libs/dayjs';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useRouter } from 'next/router';
import { AnimatePresence, motion } from 'framer-motion';
import Products from './Products';
import { TFunction } from 'next-i18next';
import { useProfile } from '@/contexts/profile';
import { toMoney } from '@/libs/app';
type CommentProps = {
  liveId: number;
  isAuth: boolean;
  messageData: MessageDataSocket;
  retry: boolean;
  commentList: (MessageData | Comment)[];
  defaultOffset: number;
  isShowProducts: boolean;
  hiddenProducts: () => void;
  t: TFunction;
  sendComment: (code: string) => void;
};
// eslint-disable-next-line react/display-name
const CommentPartial = forwardRef(
  (
    {
      liveId,
      isAuth,
      messageData,
      commentList,
      defaultOffset,
      isShowProducts,
      hiddenProducts,
      t,
      sendComment,
    }: CommentProps,
    ref
  ) => {
    const profile: any = useProfile();
    const userId = profile?.id || null;
    const [offset, setOffset] = useState<number>(0);
    const [comments, setComments] = useState<(MessageData | Comment)[]>(commentList);
    const [selected, setSelected] = useState<string>('');
    const router = useRouter();
    const filterCommentOption = [
      { label: 'Phù hợp nhất', value: 'my' },
      { label: 'Xem tất cả', value: 'all' },
    ];
    const commentEl = useRef<any>(null);
    const { refetch: refetchComments, isLoading: isLoadingComments } = useFetchCommentsQuery(
      { id: +liveId, offset: offset },
      {
        enabled: false,
        onError: ({ message }) => {
          toast.error(message);
        },
        onSuccess: ({ data }) => {
          const _comments = Array.isArray(data) ? data : [];
          // setComments(comments.sort((a, b) => a.id - b.id));
          _comments.forEach((comment) => {
            setComments((prev) => {
              if (!prev.find((p) => p.id === comment.id)) return [...prev, comment];
              else return [...prev];
            });
          });
        },
      }
    );
    const { refetch: refetchFirstComments } = useFetchCommentsQuery(
      { id: +liveId, offset: 0 },
      {
        enabled: false,
        onError: ({ message }) => {
          toast.error(message);
        },
        onSuccess: ({ data }) => {
          const comments = Array.isArray(data) ? data : [];
          // setComments(comments.sort((a, b) => a.id - b.id));
          setComments(comments);
        },
      }
    );
    const { refetch: refetchComment } = useFetchCommentByIdQuery(
      router.query?.comment_id ? +router.query?.comment_id : 0,
      {
        enabled: false,
        onError: ({ message }) => {
          toast.error(message);
        },
        onSuccess: ({ data }) => {
          if (typeof data == 'object') setComments([data]);
          // setComments((prev) => prev.concat(comments));
        },
      }
    );
    const scrollToBottom = () => {
      const parent = document.getElementById('scrollableDiv');
      if (parent && commentEl && comments.length) {
        parent.scrollTop = commentEl.current.scrollHeight;
      }
    };
    const loadFunc = () => {
      setOffset((prev) => prev + 20);
    };
    const removeEventLottery = () => {
      setTimeout(() => {
        setComments((prev: any) => {
          let arr = prev.filter((c: any) => c.type !== 'lottery');
          return [...arr];
        });
      }, 10000);
    };
    const handleSelect = (data: any) => {
      setSelected(data.value);
      if (!isAuth) return;
      scrollToBottom();
      setOffset(0);
      if (data.value === filterCommentOption[0].value) refetchComment();
      else {
        refetchFirstComments();
      }
    };

    useEffect(() => {
      setComments(commentList);
    }, [commentList]);
    useImperativeHandle(ref, () => ({
      scrollToBottom: () => {
        scrollToBottom();
      },
    }));
    useEffect(() => {
      scrollToBottom();
      setOffset(defaultOffset);
      if (router.query?.comment_id) {
        setSelected(filterCommentOption[0].value);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveId, defaultOffset, router.query?.comment_id]);
    useEffect(() => {
      if (!messageData) return;

      if (
        messageData.method === CommentMethod.CreateComment ||
        messageData.method === CommentMethod.UnHideComment
      ) {
        if (!messageData.data.comment) return;
        scrollToBottom();
        return setComments((prev) => {
          return [messageData?.data, ...prev];
        });
      }

      if (
        messageData.method === CommentMethod.DeleteComment ||
        messageData.method === CommentMethod.HideComment
      ) {
        scrollToBottom();

        setComments((prev) => {
          let _c = prev.filter((v) => v.id !== messageData?.data.comment_id);
          return _c;
        });
      }

      if (
        messageData.method === CommentMethod.CreateReplyComment ||
        messageData.method === CommentMethod.UnHideReplyComment
      ) {
        setComments((prev) => {
          let _c = forEach(prev, (m) => {
            if (m.id === messageData.data.parent_id) {
              m.reply_comments
                ? m.reply_comments.push(messageData?.data)
                : (m.reply_comments = [messageData?.data]);
            }
          });
          return _c;
        });
      }

      if (
        messageData.method === CommentMethod.DeleteReplyComment ||
        messageData.method === CommentMethod.HideReplyComment
      ) {
        setComments((prev) => {
          let _c = forEach(prev, (m) => {
            if (m.id === messageData.data.comment_id) {
              m.reply_comments = m?.reply_comments?.filter(
                (c) => c.id !== messageData.data.reply_comment_id
              );
            }
          });
          return _c;
        });
      }
      if (messageData.method === CommentMethod.UpdateComment) {
        setComments((prev) => {
          let _c = forEach(prev, (m) => {
            if (m.id === messageData.data.id) {
              m.comment = messageData.data.comment;
            }
          });
          return _c;
        });
      }
      if (messageData.method === CommentMethod.UpdateReplyComment) {
        setComments((prev) => {
          let _c = forEach(prev, (m) => {
            if (m.id === messageData.data.parent_id) {
              let c = m?.reply_comments?.find((c) => c.id === messageData.data.id);
              if (c) c.comment = messageData.data.comment;
            }
          });
          return _c;
        });
      }
      if (messageData.method === LotterySocketMethod.OpenLottery) {
        setComments((prev: any) => {
          let _m = {
            ...messageData.data,
            comment: 'Bắt đầu xổ số',
            type: 'lottery',
          };
          return [_m, ...prev];
        });
      }
      if (messageData.method === LotterySocketMethod.EndLottery) {
        setComments((prev: any) => {
          let _m = {
            ...messageData.data,
            comment: 'Kết thúc xổ số',
            type: 'lottery',
          };
          return [_m, ...prev];
        });
        removeEventLottery();
      }
      if (messageData.method === LotterySocketMethod.CloseListLottery) {
        setComments((prev) => {
          let _m = {
            ...messageData.data,
            comment: 'Chốt danh sách xổ số',
            type: 'lottery',
          };
          return [_m, ...prev];
        });
      }
      if (messageData.method === LotterySocketMethod.AdminPickTicketLottery) {
        setComments((prev) => {
          let _m = {
            ...messageData.data,
            comment: 'Kết quả sổ số: ' + messageData.data.number,
            type: 'lottery',
          };
          return [_m, ...prev];
        });
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageData, messageData?.data.id]);

    useEffect(() => {
      if (!isAuth) return;
      if (!isLoadingComments && offset && selected !== filterCommentOption[0].value) {
        refetchComments();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, isLoadingComments, isAuth, selected]);

    const getCommentUser = (comment: MessageData | Comment) => {
      const { users, facebook_comment_info, youtube_comment_info, tiktok_comment_info } = comment;

      if (users) {
        return users;
      }

      if (facebook_comment_info) {
        return {
          fullname: facebook_comment_info?.facebook_user.first_name || '',
          avatar: facebook_comment_info?.facebook_user.avatar_url || '',
        };
      }

      if (youtube_comment_info) {
        return {
          fullname: youtube_comment_info?.youtube_user.first_name || '',
          avatar: youtube_comment_info?.youtube_user.avatar_url || '',
        };
      }

      if (tiktok_comment_info) {
        return {
          fullname: tiktok_comment_info?.tiktok_user.first_name || '',
          avatar: tiktok_comment_info?.tiktok_user.avatar_url || '',
        };
      }

      return null;
    };

    const renderCampaignQuizWinComment = (comment: any) => {
      const commentAttachment = comment?.comment_attachments?.[0];

      if (!commentAttachment) return <></>;

      const prize = commentAttachment.data?.prize;
      const wonComments = commentAttachment.data?.won_comments ?? [];

      return (
        <div>
          {wonComments.map((i: any) => (
            <div key={`comment-${i.id}`}>
              <p className="text-user-chat tw-text-13 tw-font-bold tw-flex tw-items-center tw-gap-1">
                {getCommentUser(i)?.fullname}
                {i.facebook_comment_info && (
                  <Image
                    src="/facebook-color.png"
                    alt="Facebook"
                    className="tw-w-4 tw-h-4 tw-rounded-full"
                  />
                )}
                {i.youtube_comment_info && (
                  <Image
                    src="/youtube.svg"
                    alt="Youtube"
                    className="tw-w-4 tw-h-4 tw-rounded-full"
                  />
                )}
              </p>
            </div>
          ))}
          <p className="tw-text-secondary-70 tw-text-13 tw-font-normal">
            Bạn đã nhận được {prize?.reward_content} từ An Thư
          </p>
        </div>
      );
    };

    return (
      <>
        {router.query?.comment_id && (
          <div className="tw-w-max tw-z-10 tw-bg-muted-90">
            <SelectCustom
              className="tw-h-7 tw-text-xs !tw-px-2 !tw-w-40"
              options={filterCommentOption}
              handleSelect={handleSelect}
            />
          </div>
        )}
        <div className="tw-relative tw-w-full tw-flex-1 tw-overflow-hidden tw-block">
          <div
            id="scrollableDiv"
            className="tw-relative"
            style={{
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column-reverse',
              height: '100%',
            }}
          >
            <InfiniteScroll
              dataLength={comments.length}
              next={loadFunc}
              style={{
                display: 'flex',
                height: '100%',
                flexDirection: 'column-reverse',
              }} //To put endMessage and loader to the top.
              inverse={true} //
              hasMore={true}
              loader={<></>}
              // className="tw-gap-y-4"
              scrollableTarget="scrollableDiv"
              ref={commentEl}
            >
              {comments.map((item: any) => {
                const {
                  comment,
                  id,
                  staffs,
                  type,
                  reply_comments,
                  facebook_comment_info,
                  youtube_comment_info,
                  tiktok_comment_info,
                  created_at,
                  comment_attachments,
                } = item;

                const _user = getCommentUser(item);

                const replyComments =
                  Array.from(new Set(reply_comments)).length > 0
                    ? reply_comments?.sort((a: any, b: any) => {
                        return (
                          a && new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
                        );
                      })
                    : [];
                const { fullname: fullName, avatar } = getCommentAuthor(
                  _user,
                  staffs ? staffs : null
                );
                if (!comment) return <></>;

                if (type === 'lottery')
                  return (
                    <div
                      className="tw-flex tw-justify-center tw-mt-4"
                      key={comment + '-' + new Date().getTime()}
                    >
                      {item.number ? (
                        <>
                          <span
                            className="tw-text-secondary-91 tw-flex tw-items-center tw-text-13 tw-py-2 tw-px-9 tw-rounded-2xl"
                            style={{ background: 'rgba(0, 0, 0, 0.06)' }}
                          >
                            {comment}
                            {[1, 2, 3].map((i) => (
                              <Image
                                key={`i-${1}`}
                                src="/confetti-flat.gif"
                                alt="confetti"
                                className={`tw-w-6 tw-h-6 ${i == 1 && ' tw-ml-1'}`}
                              ></Image>
                            ))}
                          </span>
                        </>
                      ) : (
                        <>
                          {' '}
                          <span
                            className="tw-text-secondary-91 tw-text-13 tw-py-2 tw-px-9 tw-rounded-2xl"
                            style={{ background: 'rgba(0, 0, 0, 0.06)' }}
                          >
                            {comment}
                          </span>
                        </>
                      )}
                    </div>
                  );

                return (
                  <div key={'comment-' + id + '-' + created_at}>
                    <div className="tw-flex tw-gap-x-2 tw-w-full tw-mt-4 tw-items-end">
                      <Avatar
                        alt={fullName}
                        src={avatar || ''}
                        className="tw-w-8 tw-h-8 tw-mt-6"
                        rounded="tw-rounded-full"
                      />
                      <div className="tw-flex-1">
                        <div className="tw-w-full">
                          <div className="tw-bg-white tw-px-4 tw-py-1  tw-rounded-t-lg tw-rounded-br-lg tw-relative custom-polygon-bottom">
                            <div className="text-user-chat tw-font-bold tw-text-13 tw-flex tw-items-center">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html:
                                    item.comment_attachments &&
                                    item.comment_attachments[0] &&
                                    [
                                      'AUCTION_TOP_OFFER_HIGHEST_PRICE',
                                      'CAMPAIGN_QUIZ_WIN',
                                    ].includes(item.comment_attachments[0]?.type)
                                      ? 'An Thư Kim Cương'
                                      : fullName,
                                }}
                              ></div>
                              {!!facebook_comment_info && (
                                <Image
                                  className="tw-w-3 tw-h-3 tw-ml-2"
                                  src="/fb-mini.png"
                                  alt="fb-min"
                                ></Image>
                              )}
                              {!!youtube_comment_info && (
                                <Image
                                  className="tw-w-3 tw-h-3 tw-ml-2"
                                  src="/youtube-mini.png"
                                  alt="ytb-min"
                                ></Image>
                              )}
                              {!!tiktok_comment_info && (
                                <Image
                                  className="tw-w-3 tw-h-3 tw-ml-2"
                                  src="/tiktok-mini.webp"
                                  alt="ttk-min"
                                ></Image>
                              )}
                            </div>
                            <p className="tw-text-secondary-70 tw-text-13 tw-font-normal">
                              {comment}
                            </p>

                            {/* AUCTION */}
                            {item.comment_attachments &&
                              item.comment_attachments.length > 0 &&
                              item.comment_attachments[0]?.type ===
                                'AUCTION_TOP_OFFER_HIGHEST_PRICE' &&
                              item.comment_attachments[0]?.data?.user_auction && (
                                <div
                                  className="tw-py-2 tw-text-13 tw-w-full tw-bg-white tw-rounded tw-mx-auto"
                                  style={{ minWidth: 350 }}
                                >
                                  {item.comment_attachments[0]?.data?.user_auction
                                    .sort((a: any, b: any) => b.price - a.price)
                                    .slice(0, 3)
                                    .map((u: any, i: number) => {
                                      const name =
                                        u.type === 'FACEBOOK'
                                          ? u.facebook_user?.first_name
                                          : u.users?.fullname;
                                      const avatar =
                                        u.type === 'FACEBOOK'
                                          ? u.facebook_user?.avatar_url
                                          : u.users?.avatar;
                                      return (
                                        <div
                                          className="tw-flex tw-items-center tw-py-1 tw-gap-1 tw-text-[11px]"
                                          key={`top-auction-${name}-${u.price}`}
                                        >
                                          <span>{i + 1}.</span>
                                          <Avatar
                                            alt={name}
                                            src={avatar || ''}
                                            className="tw-w-5 tw-h-5 tw-mx-1"
                                            rounded="tw-rounded-full"
                                          />
                                          <span>{name}</span>
                                          <span>-</span>
                                          <span className="tw-font-bold">
                                            {toMoney(u.price, t, PriceSymbol.$)}
                                          </span>
                                          <span>/</span>
                                          <span>
                                            <span className="tw-text-secondary-50">Thời gian:</span>{' '}
                                            <b>
                                              {u.created_at
                                                ? formatTimeHMSS(u.created_at)
                                                : '--:--:--'}
                                            </b>
                                          </span>
                                        </div>
                                      );
                                    })}
                                </div>
                              )}

                            {/* QUIZ */}
                            {comment_attachments &&
                              comment_attachments[0] &&
                              comment_attachments[0].type === 'CAMPAIGN_QUIZ_WIN' &&
                              comment_attachments[0].data?.won_comments &&
                              renderCampaignQuizWinComment(item)}

                            {created_at && (
                              <div
                                className={`tw-text-right tw-text-secondary-50`}
                                style={{
                                  fontSize: '10px',
                                }}
                              >
                                {humanTimeDate(created_at)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="tw-flex">
                      <div className="tw-w-9"></div>
                      <div className="tw-flex-1">
                        <div className="tw-w-3/4">
                          <div>
                            {replyComments && replyComments?.length > 0 && (
                              <>
                                <div className="tw-flex tw-flex-col tw-mt-2 tw-gap-y-2">
                                  {replyComments?.map((rC: any) => {
                                    return (
                                      rC && (
                                        <ReplyComment
                                          fullName={fullName}
                                          replyComment={rC}
                                          key={`rep-${id}-${rC?.id}`}
                                        />
                                      )
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </InfiniteScroll>
          </div>
          <AnimatePresence>
            {isShowProducts && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: '100%', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="tw-absolute tw-bottom-0 tw-left-0 tw-bg-transparent tw-h-full tw-w-full tw-rounded-lg tw-max-h-full"
              >
                <Products
                  userId={userId}
                  sendComment={sendComment}
                  onClose={hiddenProducts}
                  liveId={liveId}
                  t={t}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </>
    );
  }
);
export default CommentPartial;
